import { useState, useCallback, useEffect, useRef } from 'react';
import { apiClient } from '../../../services/api/apiService';
import matchService from '../../../services/api/matchService';
import { PAGE_SIZE } from '../leagueGames';

/**
 * Custom hook for managing league details data
 * Handles fetching teams, schedule, standings, and matches for a specific league
 */
export const useLeagueDetails = (leagueId) => {
  const [teams, setTeams] = useState([]);
  const [schedule, setSchedule] = useState([]);
  const [standings, setStandings] = useState([]);
  // How the table is ranked (rank by wins or points, draws, sets): PR 8
  const [standingsRules, setStandingsRules] = useState(null);
  const [leagueMatches, setLeagueMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leagueMatchesLoading, setLeagueMatchesLoading] = useState(false);
  // { view, total, counts } of the loaded schedule / matches page
  const [scheduleMeta, setScheduleMeta] = useState({ view: 'upcoming', total: 0, counts: null });
  const [matchesMeta, setMatchesMeta] = useState({ view: 'upcoming', total: 0, counts: null });
  const [loadingMore, setLoadingMore] = useState(false);
  const scheduleRef = useRef(schedule);
  scheduleRef.current = schedule;
  const matchesRef = useRef(leagueMatches);
  matchesRef.current = leagueMatches;
  const scheduleViewRef = useRef('upcoming');
  const matchesViewRef = useRef('upcoming');
  const [confirmedMatches, setConfirmedMatches] = useState(new Set());
  const [availableTeams, setAvailableTeams] = useState([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState([]);

  // Fetch league teams (`quiet`: refresh the tab count without the loading state)
  const fetchLeagueTeams = useCallback(async ({ quiet = false } = {}) => {
    if (!leagueId) return;
    quiet = quiet === true;
    try {
      if (!quiet) setLoading(true);
      const response = await apiClient.get(`/leagues/${leagueId}/teams`);
      setTeams(response.data?.data || []);
    } catch (error) {
      console.error('Error fetching league teams:', error);
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [leagueId]);

  // Fetch league schedule: one page of the games still to play ('upcoming')
  // or of past nights ('past'); `append` loads the next page (Load more).
  // `quiet` refreshes the tab counts without the loading state.
  const fetchLeagueSchedule = useCallback(async ({ view, append = false, quiet = false } = {}) => {
    if (!leagueId) return;
    const nextView = typeof view === 'string' ? view : scheduleViewRef.current;
    scheduleViewRef.current = nextView;
    append = append === true;
    const offset = append ? scheduleRef.current.length : 0;
    try {
      if (append) setLoadingMore(true);
      else if (!quiet) setLoading(true);
      // _t cache-busts the request so the browser never reuses a stale 304 response
      const response = await apiClient.get(`/leagues/${leagueId}/schedule`, {
        params: { view: nextView, limit: PAGE_SIZE, offset, _t: Date.now() },
      });
      const rows = response.data?.data || [];
      setSchedule((prev) => (append ? [...prev, ...rows] : rows));
      setScheduleMeta({
        view: nextView,
        total: response.data?.total ?? offset + rows.length,
        counts: response.data?.counts || null,
      });
    } catch (error) {
      console.error('Error fetching league schedule:', error);
    } finally {
      if (append) setLoadingMore(false);
      else if (!quiet) setLoading(false);
    }
  }, [leagueId]);

  // Fetch league standings
  const fetchLeagueStandings = useCallback(async () => {
    if (!leagueId) return;
    try {
      setLoading(true);
      const response = await apiClient.get(`/leagues/${leagueId}/standings`);
      setStandings(response.data?.data || []);
      setStandingsRules(response.data?.rules || null);
    } catch (error) {
      console.error('Error fetching league standings:', error);
    } finally {
      setLoading(false);
    }
  }, [leagueId]);

  // Fetch league matches: only this league's, one page of 'upcoming' (live
  // and scheduled) or 'completed', filtered and paged by the server (LG-B23)
  const fetchLeagueMatches = useCallback(async ({ view, append = false } = {}) => {
    if (!leagueId) return;
    const nextView = typeof view === 'string' ? view : matchesViewRef.current;
    matchesViewRef.current = nextView;
    append = append === true;
    const offset = append ? matchesRef.current.length : 0;
    try {
      if (append) setLoadingMore(true);
      else setLeagueMatchesLoading(true);
      const response = await apiClient.get(`/leagues/${leagueId}/matches`, {
        params: { view: nextView, limit: PAGE_SIZE, offset },
      });
      const rows = response.data?.data || [];
      setLeagueMatches((prev) => (append ? [...prev, ...rows] : rows));
      setMatchesMeta({
        view: nextView,
        total: response.data?.total ?? offset + rows.length,
        counts: response.data?.counts || null,
      });
    } catch (error) {
      console.error('Error fetching league matches:', error);
    } finally {
      if (append) setLoadingMore(false);
      else setLeagueMatchesLoading(false);
    }
  }, [leagueId]);

  useEffect(() => {
    if (leagueId) {
      fetchLeagueMatches();
      // Tab label counts (teams, "Schedule (n)") load with the league, whatever tab opens first
      fetchLeagueTeams({ quiet: true });
      fetchLeagueSchedule({ quiet: true });
    }
  }, [leagueId, fetchLeagueMatches, fetchLeagueSchedule, fetchLeagueTeams]);

  // Fetch available teams
  const fetchAvailableTeams = useCallback(async (sport) => {
    if (!leagueId) return;
    try {
      const response = await apiClient.get('/teams');
      const allTeams = response.data?.data || [];
      const leagueTeamIds = new Set(teams.map(t => t.id));
      
      const available = allTeams.filter(team => {
        const isNotInLeague = !leagueTeamIds.has(team.id);
        const isSameSport = team.sport_type === sport;
        return isNotInLeague && isSameSport;
      });
      
      setAvailableTeams(available);
    } catch (error) {
      console.error('Error fetching available teams:', error);
    }
  }, [leagueId, teams]);

  // Add team to league
  const addTeamToLeague = useCallback(async (teamId) => {
    if (!leagueId) return;
    try {
      await apiClient.post(`/leagues/${leagueId}/teams`, { team_id: teamId });
      await fetchLeagueTeams();
    } catch (error) {
      throw error;
    }
  }, [leagueId, fetchLeagueTeams]);

  // Remove team from league
  const removeTeamFromLeague = useCallback(async (teamId) => {
    if (!leagueId) return;
    try {
      await apiClient.delete(`/leagues/${leagueId}/teams/${teamId}`);
      await fetchLeagueTeams();
    } catch (error) {
      throw error;
    }
  }, [leagueId, fetchLeagueTeams]);

  // Generate schedule
  const generateSchedule = useCallback(async () => {
    if (!leagueId) return;
    try {
      await apiClient.post(`/leagues/${leagueId}/generate-schedule`);
      await fetchLeagueSchedule();
    } catch (error) {
      throw error;
    }
  }, [leagueId, fetchLeagueSchedule]);

  // Clear schedule
  const clearSchedule = useCallback(async () => {
    if (!leagueId) return;
    try {
      await apiClient.delete(`/leagues/${leagueId}/clear-schedule`);
      await fetchLeagueSchedule();
    } catch (error) {
      throw error;
    }
  }, [leagueId, fetchLeagueSchedule]);

  // Create custom schedule
  const createCustomSchedule = useCallback(async (schedule, config) => {
    if (!leagueId) return;
    try {
      await apiClient.post(`/leagues/${leagueId}/custom-schedule`, { schedule, config });
      await fetchLeagueSchedule();
    } catch (error) {
      throw error;
    }
  }, [leagueId, fetchLeagueSchedule]);

  // Start match
  const startMatch = useCallback(async (gameId) => {
    try {
      await matchService.startMatch(gameId);
      await fetchLeagueMatches();
    } catch (error) {
      throw error;
    }
  }, [fetchLeagueMatches]);

  // Update game score
  const updateGameScore = useCallback(async (gameId, score1, score2, status) => {
    try {
      await apiClient.put(`/leagues/games/${gameId}/score`, {
        team_1_score: score1,
        team_2_score: score2,
      });
      setConfirmedMatches(prev => new Set([...prev, gameId]));
      await fetchLeagueMatches();
      await fetchLeagueStandings();
    } catch (error) {
      throw error;
    }
  }, [fetchLeagueMatches, fetchLeagueStandings]);

  return {
    teams,
    setTeams,
    schedule,
    setSchedule,
    standings,
    setStandings,
    standingsRules,
    leagueMatches,
    setLeagueMatches,
    scheduleMeta,
    matchesMeta,
    loadingMore,
    loading,
    setLoading,
    leagueMatchesLoading,
    confirmedMatches,
    setConfirmedMatches,
    availableTeams,
    setAvailableTeams,
    selectedTeamIds,
    setSelectedTeamIds,
    fetchLeagueTeams,
    fetchLeagueSchedule,
    fetchLeagueStandings,
    fetchLeagueMatches,
    fetchAvailableTeams,
    addTeamToLeague,
    removeTeamFromLeague,
    generateSchedule,
    clearSchedule,
    createCustomSchedule,
    startMatch,
    updateGameScore
  };
};
